import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApplicationsTable, StudentDetailsModal } from './components';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Application, Student } from './types';
import FilterSortPanel from '../../ui/FilterSortPanel';
import { EmailSenderModal } from './components/EmailSenderModal';
import { AppPagination } from '../../ui/AppPagination';

import SyncIcon from '@mui/icons-material/Sync';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const APPLICATIONS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/`;
const SEARCH_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/search`;
const SYNC_PROGRAMS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/programs/sync`;
const EXAM_FOLDERS_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1/applications/exam-folders`;

// Ключ для хранения фильтров в localStorage
const FILTERS_STORAGE_KEY = 'umkc_application_list_filters';

interface ApplicationSearchQuery {
  id: number | null; // Добавлено поле ID заявки для API
  organization_name: string | null;
  student_query: string | null;
  specialist_query: string | null;
  start_date_from: string | null;
  start_date_to: string | null;
  status: string | null;
  exam_export_folder: string | null;
  sort_by: string | null;
  sort_order: string | null;
}

// Интерфейс для структуры сохраняемых данных в localStorage
interface StoredFiltersState {
  idSearch: string; // Добавлено
  organizationSearch: string;
  studentSearch: string;
  specialistSearch: string;
  statusFilter: string | null;
  examFolderFilter: string | null;
  startDate: string | null;
  endDate: string | null;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  appliedFilters: Omit<ApplicationSearchQuery, 'sort_by' | 'sort_order'>;
}

interface ApiApplicationResponse {
  total: number;
  page: number;
  size: number;
  items: any[];
}

interface ApplicationListProps {
  onLogout: () => void;
}

// Хелпер для безопасного получения сохраненного состояния
const getStoredFilters = (): Partial<StoredFiltersState> => {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Ошибка при чтении фильтров из localStorage:', error);
    return {};
  }
};

const ApplicationList: React.FC<ApplicationListProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [isSyncStatusVisible, setIsSyncStatusVisible] = useState(false);

  const [applications, setApplications] = useState<Application[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [pageSize] = useState<number>(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppForModal, setSelectedAppForModal] = useState<Application | null>(null);

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedAppForEmail, setSelectedAppForEmail] = useState<Application | null>(null);

  // Список всех уникальных папок, загружаемых с бэкенда
  const [examFolders, setExamFolders] = useState<string[]>([]);

  // Считываем сохраненное состояние один раз при монтировании
  const storedState = getStoredFilters();

  // Инициализация состояний значениями из хранилища (или дефолтными)
  const [idSearch, setIdSearch] = useState<string>(() => storedState.idSearch ?? ''); // Добавлено
  const [organizationSearch, setOrganizationSearch] = useState<string>(() => storedState.organizationSearch ?? '');
  const [studentSearch, setStudentSearch] = useState<string>(() => storedState.studentSearch ?? '');
  const [specialistSearch, setSpecialistSearch] = useState<string>(() => storedState.specialistSearch ?? '');
  const [statusFilter, setStatusFilter] = useState<string | null>(() => storedState.statusFilter ?? null);
  const [examFolderFilter, setExamFolderFilter] = useState<string | null>(() => storedState.examFolderFilter ?? null);

  const [startDate, setStartDate] = useState<Date | null>(() =>
    storedState.startDate && !isNaN(Date.parse(storedState.startDate))
      ? new Date(storedState.startDate)
      : null
  );
  const [endDate, setEndDate] = useState<Date | null>(() =>
    storedState.endDate && !isNaN(Date.parse(storedState.endDate))
      ? new Date(storedState.endDate)
      : null
  );

  const [sortBy, setSortBy] = useState<string>(() => storedState.sortBy ?? 'submission_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => storedState.sortOrder ?? 'desc');

  const [page, setPage] = useState<number>(() => storedState.page ?? 1);

  const [appliedFilters, setAppliedFilters] = useState<Omit<ApplicationSearchQuery, 'sort_by' | 'sort_order'>>(() =>
    storedState.appliedFilters ?? {
      id: null, // Добавлено
      organization_name: null,
      student_query: null,
      specialist_query: null,
      status: null,
      exam_export_folder: null,
      start_date_from: null,
      start_date_to: null,
    }
  );

  // Сохранение состояния фильтров в localStorage при любом их изменении
  useEffect(() => {
    const stateToStore: StoredFiltersState = {
      idSearch, // Добавлено
      organizationSearch,
      studentSearch,
      specialistSearch,
      statusFilter,
      examFolderFilter,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      sortBy,
      sortOrder,
      page,
      appliedFilters,
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(stateToStore));
  }, [
    idSearch, // Добавлено
    organizationSearch,
    studentSearch,
    specialistSearch,
    statusFilter,
    examFolderFilter,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    page,
    appliedFilters,
  ]);

  // Вычисляем, активен ли хотя бы один фильтр/поиск/сортировка, отличная от дефолтной
  const isResetDisabled =
    !idSearch && // Добавлено
    !organizationSearch &&
    !studentSearch &&
    !specialistSearch &&
    statusFilter === null &&
    examFolderFilter === null &&
    startDate === null &&
    endDate === null &&
    sortBy === 'submission_date' &&
    sortOrder === 'desc' &&
    appliedFilters.id === null && // Добавлено
    appliedFilters.organization_name === null &&
    appliedFilters.student_query === null &&
    appliedFilters.specialist_query === null &&
    appliedFilters.status === null &&
    appliedFilters.exam_export_folder === null &&
    appliedFilters.start_date_from === null &&
    appliedFilters.start_date_to === null;

  const user = JSON.parse(localStorage.getItem('user') || '{}') as Partial<{
    username: string;
    full_name: string;
    role?: 'user' | 'admin';
  }>;
  const username = user?.full_name || user?.username || '—';
  const role = user?.role || 'user';

  const mapStatusToUi = (apiStatus: string): string => {
    switch (apiStatus.toLowerCase()) {
      case 'new': return 'Новая';
      case 'sent': return 'Отправлено';
      case 'completed': return 'Завершена';
      case 'rejected': return 'Отклонена';
      case 'in_progress': return 'В обработке';
      default: return apiStatus;
    }
  };

  useEffect(() => {
    let fadeOutTimer: NodeJS.Timeout;
    let removeTimer: NodeJS.Timeout;

    if (syncSuccess) {
      setIsSyncStatusVisible(true);
      fadeOutTimer = setTimeout(() => setIsSyncStatusVisible(false), 2000);
      removeTimer = setTimeout(() => setSyncSuccess(null), 2300);
    }

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(removeTimer);
    };
  }, [syncSuccess]);

  const handleSyncPrograms = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      onLogout();
      return;
    }

    setIsSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);
    setIsSyncStatusVisible(false);

    try {
      const response = await fetch(SYNC_PROGRAMS_URL, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          onLogout();
          return;
        }
        throw new Error(`Ошибка синхронизации: ${response.status}`);
      }

      const data = await response.json().catch(() => null as any);

      const updated =
        (data && (data.updated ?? data.updated_count)) ?? 0;
      const created =
        (data && (data.created ?? data.created_count)) ?? 0;

      const message = `Программы успешно обновлены: изменено ${updated}, добавлено новых ${created}.`;

      setSyncError(null);
      setSyncSuccess(message);
      setIsSyncStatusVisible(true);
    } catch (error) {
      console.error('Error syncing programs:', error);
      setSyncSuccess(null);
      setSyncError('Не удалось обновить программы.');
      setIsSyncStatusVisible(true);
    } finally {
      setIsSyncing(false);
    }
  };

  // Функция загрузки списка уникальных папок
  const fetchExamFolders = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    try {
      const response = await fetch(EXAM_FOLDERS_URL, {
        method: 'GET',
        headers: {
          "Accept": "application/json",
          "Authorization": token
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          onLogout();
          return;
        }
        throw new Error(`Ошибка загрузки папок: ${response.status}`);
      }

      const data = await response.json();
      if (data && Array.isArray(data.folders)) {
        setExamFolders(data.folders);
      }
    } catch (error) {
      console.error('Error fetching exam folders:', error);
    }
  }, [onLogout]);

  // Загружаем папки при монтировании
  useEffect(() => {
    fetchExamFolders();
  }, [fetchExamFolders]);

  const fetchApplications = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      return;
    }

    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      });

      const body: ApplicationSearchQuery = {
        ...appliedFilters,
        sort_by: sortBy === 'createdAt' ? 'submission_date' : sortBy,
        sort_order: sortOrder
      };

      const response = await fetch(`${SEARCH_URL}?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": token
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        if (response.status === 401) {
          onLogout();
          return;
        }
        const errorText = await response.text();
        console.error("API Error:", errorText);
        throw new Error(`Ошибка загрузки заявок: ${response.status}`);
      }

      const data: ApiApplicationResponse = await response.json();

      const uiApplications: Application[] = (data.items || []).map((app: any) => {
        const orgName = app.organization?.short_name || app.organization?.full_name || `ID: ${app.organization_id}`;
        const specName = app.responsible_specialist?.full_name || `ID: ${app.responsible_specialist_id}`;
        const specEmail = app.responsible_specialist?.email || '';
        const specId = app.responsible_specialist?.id || app.responsible_specialist_id;

        const studentsList: Student[] = (app.students || []).map((s: any) => ({
          id: s.id,
          fullName: s.full_name,
          email: s.email,
          login: s.login,
          password: s.password,
          phone: s.phone,
          snils: s.snils,
          birthDate: s.birth_date,
          address: s.address,
          position: s.position,
          programs: s.student_programs?.map((sp: any) => ({
            name: sp.program?.name || 'Без названия',
            startDate: sp.start_date,
            endDate: sp.end_date,
            examDate: sp.exam_date
          })) || []
        }));

        return {
          id: String(app.id),
          status: mapStatusToUi(app.status),
          organization: orgName,
          specialist: specName,
          specialistId: specId,
          specialistEmail: specEmail,
          students: studentsList,
          examFolder: app.exam_export_folder || '—',
          createdAt: app.submission_date || app.created_at
        };
      });

      setApplications(uiApplications);

      const apiReportedTotal = typeof data.total === 'number' ? data.total : 0;
      const computedTotal = apiReportedTotal > 0 ? apiReportedTotal : uiApplications.length;
      setTotalItems(computedTotal);

    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedFilters, sortBy, sortOrder, onLogout]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const formatDateForApi = (date: Date | null): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSearchClick = () => {
    setPage(1);

    // Преобразуем строку ID в число или null
    const parsedId = idSearch.trim() ? parseInt(idSearch.trim(), 10) : null;
    const appId = (parsedId !== null && !isNaN(parsedId)) ? parsedId : null;

    setAppliedFilters({
      id: appId, // Добавлено в фильтрацию для отправки на бэк
      organization_name: organizationSearch || null,
      student_query: studentSearch || null,
      specialist_query: specialistSearch || null,
      status: statusFilter || null,
      exam_export_folder: examFolderFilter || null,
      start_date_from: formatDateForApi(startDate),
      start_date_to: formatDateForApi(endDate),
    });
  };

  const handleReset = () => {
    setIdSearch(''); // Добавлено
    setOrganizationSearch('');
    setStudentSearch('');
    setSpecialistSearch('');
    setStatusFilter(null);
    setExamFolderFilter(null);
    setStartDate(null);
    setEndDate(null);
    setSortBy('submission_date');
    setSortOrder('desc');

    setPage(1);
    setAppliedFilters({
      id: null, // Добавлено
      organization_name: null,
      student_query: null,
      specialist_query: null,
      status: null,
      exam_export_folder: null,
      start_date_from: null,
      start_date_to: null,
    });

    // Очищаем localStorage
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleViewStudents = (applicationId: string) => {
    const app = applications.find(a => a.id === applicationId);
    if (app) {
      setSelectedAppForModal(app);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppForModal(null);
  };

  const handleSendEmail = (applicationId: string) => {
    const app = applications.find(a => a.id === applicationId);
    if (app) {
      setSelectedAppForEmail(app);
      setIsEmailModalOpen(true);
    }
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setSelectedAppForEmail(null);
    fetchApplications();
  };

  const handleGenerateDocument = (applicationId: string) => {
    navigate(`/applications/${applicationId}/documents/generate`);
  };

  const handleDelete = async (applicationId: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту заявку?')) return;

    const token = localStorage.getItem('token');
    if (!token) {
      onLogout();
      return;
    }

    try {
      const response = await fetch(`${APPLICATIONS_URL}${applicationId}`, {
        method: 'DELETE',
        headers: {
          "Accept": "application/json",
          "Authorization": token
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          onLogout();
          return;
        }
        throw new Error(`Ошибка при удалении: ${response.status}`);
      }
      fetchApplications();
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Ошибка при удалении заявки.');
    }
  };

  const statusOptions = [
    { value: 'new', label: 'Новая' },
    { value: 'sent', label: 'Отправлено' },
  ];

  // Маппинг полученных с бэкенда уникальных папок для выпадающего списка
  const examFolderOptions = examFolders.map((folder) => ({
    value: folder,
    label: folder,
  }));

  const sortOptions = [
    { value: 'submission_date', label: 'Дата создания' },
    { value: 'status', label: 'Статус' },
    { value: 'organization', label: 'Организация' },
    { value: 'specialist', label: 'Специалист' },
  ];

  const filtersForPanel = [
    { key: 'status', label: 'Статус заявки', options: statusOptions, currentValue: statusFilter },
    { key: 'exam_export_folder', label: 'Папка экзамена', options: examFolderOptions, currentValue: examFolderFilter },
  ];

  const visibleApplications = applications.length > pageSize
    ? applications.slice((page - 1) * pageSize, page * pageSize)
    : applications;

  const syncIsSuccess = Boolean(syncSuccess);
  const syncIsError = Boolean(syncError);

  const syncToastOpacityClass = syncIsSuccess
    ? (isSyncStatusVisible ? 'opacity-100' : 'opacity-0')
    : 'opacity-100';

  return (
    <div className="w-full bg-white pb-10">

      {(syncIsSuccess || syncIsError) && (
        <div
          className={`
            fixed top-10 left-1/2 z-50 -translate-x-1/2
            transition-opacity duration-300 ease-in-out
            ${syncToastOpacityClass}
          `}
        >
          <div
            className={`
              flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg border
              ${
                syncIsError
                  ? 'bg-red-100 text-red-800 border-red-200'
                  : 'bg-blue-100 text-blue-800 border-blue-200'
              }
            `}
          >
            {syncIsError ? (
              <ErrorOutlineIcon className="text-red-600" />
            ) : (
              <PlaylistAddCheckIcon className="text-blue-600" />
            )}
            <span className="text-sm font-semibold">
              {syncError || syncSuccess}
            </span>
          </div>
        </div>
      )}

      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-3">
          <AssignmentIcon fontSize="large" className="text-blue-600" />
          Заявки
        </h1>

          <button
            onClick={handleSyncPrograms}
            disabled={isSyncing}
            className="group flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition-all disabled:opacity-70"
          >
            <SyncIcon
              fontSize="small"
              className={`transition-transform duration-700 ${isSyncing ? 'animate-spin' : 'group-hover:animate-spin'}`}
            />
            {isSyncing ? 'Обновление...' : 'Обновить программы'}
          </button>
        </div>

        <div className="bg-white shadow-md border border-gray-100 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Список заявок</h3>
            <button
              onClick={() => navigate('/application/new')}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Создать заявку
            </button>
          </div>

          <div className="px-6 py-4 border-b bg-gray-50 w-full">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ID заявки</label>
                <input
                  type="text"
                  value={idSearch}
                  onChange={(e) => setIdSearch(e.target.value.replace(/\D/g, ''))} // Разрешены только цифры
                  onKeyDown={handleKeyDown}
                  placeholder="ID..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Организация</label>
                <input
                  type="text"
                  value={organizationSearch}
                  onChange={(e) => setOrganizationSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Название..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Студент</label>
                <input
                  type="text"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="ФИО..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Специалист</label>
                <input
                  type="text"
                  value={specialistSearch}
                  onChange={(e) => setSpecialistSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="ФИО..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <FilterSortPanel
            showSearch={false}
            searchTerm=""
            onSearchChange={() => {}}
            onSearchSubmit={() => {}}
            filters={filtersForPanel}
            onFilterChange={(key, value) => {
              if (key === 'status') setStatusFilter(value);
              if (key === 'exam_export_folder') setExamFolderFilter(value);
            }}
            showDateRange={true}
            startDate={startDate}
            onStartDateChange={setStartDate}
            endDate={endDate}
            onEndDateChange={setEndDate}
            sortOptions={sortOptions}
            sortBy={sortBy}
            onSortByChange={setSortBy}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleReset}
              disabled={loading || isResetDisabled}
              className={`px-6 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                isResetDisabled
                  ? 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed opacity-70'
                  : 'border-gray-300 text-gray-600 bg-white hover:bg-gray-100 shadow-sm'
              }`}
            >
              Сбросить
            </button>
            <button
              onClick={handleSearchClick}
              disabled={loading}
              className={`px-6 py-2 rounded-lg text-white text-sm font-semibold transition-all duration-200 shadow-sm ${
                loading
                  ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Найти
            </button>
          </div>

          <div className="min-h-[200px]">
            {loading ? (
              <div className="flex justify-center items-center w-full py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <ApplicationsTable
                applications={visibleApplications}
                loading={false}
                onViewStudents={handleViewStudents}
                onSendEmail={handleSendEmail}
                onDelete={handleDelete}
                onGenerateDocument={handleGenerateDocument}
              />
            )}
          </div>

          {!loading && (
            <div className="border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Показано{' '}
                <span className="font-medium">
                  {totalItems > 0 ? (page - 1) * pageSize + 1 : 0}
                </span>{' '}
                -{' '}
                <span className="font-medium">
                  {Math.min(page * pageSize, totalItems)}
                </span>{' '}
                из <span className="font-medium">{totalItems}</span>
              </div>
              <AppPagination
                page={page}
                totalItems={totalItems}
                pageSize={pageSize}
                onChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      <StudentDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        applicationId={selectedAppForModal?.id || ''}
        onLogout={onLogout}
      />

      <EmailSenderModal
        isOpen={isEmailModalOpen}
        onClose={handleCloseEmailModal}
        application={selectedAppForEmail}
        onSuccess={() => fetchApplications()}
      />
    </div>
  );
};

export default ApplicationList;